/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { Category, TextValueTranslation } from '@msdyn365-commerce/retail-proxy';

import { getCategoryAction, CategoriesInput as RawCategoriesInput } from './get-categories';
import { getCategoryUrl } from '@msdyn365-commerce-modules/retail-actions';
import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input for get-categories data action.
 */
export class CategoriesInput implements IActionInput {
    public readonly maxItems: number;

    public readonly channelId: number;

    public includeRootCategory?: boolean;

    private readonly catalogId: number;

    private readonly sitePath: string;

    private readonly _mappedToHierarchy: boolean;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly locale?: string;

    public constructor(context: IRequestContext, mappedToHierarchy: boolean, maxItems?: number, includeRootCategory?: boolean) {
        this._mappedToHierarchy = mappedToHierarchy;
        this.includeRootCategory = includeRootCategory;
        this.maxItems = maxItems || 250;
        this.channelId = context && context.apiSettings && context.apiSettings.channelId ? +context.apiSettings.channelId : 0;
        this.sitePath = (context && context.sitePath) || '';
        this.apiSettings = context.apiSettings;
        this.locale = context.locale || '';
        this.catalogId = getCatalogId(context);
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'SitePath', value: this.sitePath },
            { key: 'IncludeRootCategory', value: this.includeRootCategory },
            { key: 'Top', value: this.maxItems },
            { key: 'CatalogId', value: this.catalogId },
            { key: 'Locale', value: this.locale }
        );

    public getCacheObjectType = () => (this._mappedToHierarchy ? 'CategoryHierarchy' : 'Category');

    //public dataCacheType = (): CacheType => 'request';

    public getLocale = (): string => this.locale || '';

    public dataCacheType = (): CacheType => 'none';
}

const getFriendlyName = (locale?: string, nameTranslations?: TextValueTranslation[]): string | undefined => {
    let nameTranslation: TextValueTranslation | undefined;
    if (locale && nameTranslations && nameTranslations.length > 0) {
        nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
    }

    return nameTranslation && nameTranslation.Text;
};

export interface ICategoryMap {
    [RecordId: number]: CategoryHierarchy;
}

/**
 * Creates a hierarchy of categories based on the ParentCategory property.
 * @param categoryList Categories that will be converted into a hierarchy.
 * @param ctx
 * @param locale
 * @returns Hierarchy of categories in array.
 */
export const mapCategoryToHierarchy = (
    categoryList: Category[],
    ctx: IActionContext,
    locale?: string,
    includeRootCategory?: boolean
): CategoryHierarchy[] => {
    if (!categoryList || categoryList.length === 0) {
        return [];
    }

    const categoryMap: ICategoryMap = {};

    for (const category of categoryList) {
        const localName = getFriendlyName(locale, category.NameTranslations);
        const categoryHierarchy = <CategoryHierarchy>{ ...category };
        categoryHierarchy.NeutralizedName = category.Name;
        categoryHierarchy.Name = localName || categoryHierarchy.NeutralizedName;
        categoryMap[category.RecordId] = categoryHierarchy;
    }

    let rootCategory: CategoryHierarchy | undefined;

    for (const category of Object.values(categoryMap)) {
        const parentId = category.ParentCategory;
        category.Url = getCategoryUrl(category, ctx, categoryMap);
        if (parentId === 0) {
            rootCategory = category;
            continue;
        }

        const parent = parentId && categoryMap[parentId];
        if (parent) {
            parent.Children = parent.Children || [];
            parent.Children.push(category);
        }
    }

    if (!rootCategory) {
        return [];
    }

    if (includeRootCategory) {
        const categoryHierarchy: CategoryHierarchy[] = [];
        categoryHierarchy.push(rootCategory);
        return categoryHierarchy;
    }

    return rootCategory.Children ?? [];
};

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createCategoriesHierarchyInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const topItems = inputData.config && inputData.config.topCategories && Number.parseInt(inputData.config.topCategories, 10);
    return new CategoriesInput(inputData.requestContext, true, topItems);
};

/**
 * Calls the Retail API and returns all the categories as a hierarchy.
 * @param input
 * @param ctx
 */
export async function getCategoryHierarchyAction(input: CategoriesInput, ctx: IActionContext): Promise<CategoryHierarchy[]> {
    const categories = await getCategoryAction(new RawCategoriesInput(ctx.requestContext, false, input.maxItems), ctx);
    const mappedCategories = mapCategoryToHierarchy(categories, ctx, input.getLocale(), input.includeRootCategory);
    return mappedCategories;
}

export const getCategoryHierarchyActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-categories-hierarchy',
    action: <IAction<CategoryHierarchy[]>>getCategoryHierarchyAction,
    input: createCategoriesHierarchyInput
});

export default getCategoryHierarchyActionDataAction;
